import { useForm } from '@tanstack/react-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from "@tanstack/react-router";
import type { inferRouterOutputs } from '@trpc/server';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { v7 as uuidv7 } from 'uuid';
import { z } from 'zod';
import type { AppRouter } from '~backend/trpc/router';
import AttendanceSync from '~frontend/components/AttendanceSync';
import BedAllotment from '~frontend/components/BedAllotment';
import CadetDetails from '~frontend/components/CadetDetails';
import PhotoCapture from '~frontend/components/PhotoCapture';
import { Input } from '~frontend/components/ui/input';
import { Label } from '~frontend/components/ui/label';
import { ScrollArea } from '~frontend/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '~frontend/components/ui/tabs';
import { getBaseBackendUrl } from '~frontend/lib/utils';
import { trpc } from '~frontend/router';
import { useEnrollmentStore } from '~frontend/stores/enrollmentStore';

const enrollmentSearchSchema = z.object({
    sessionId: z.string().optional(),
})

export const Route = createFileRoute('/_LoggedIn/cadet/enrollment')({
    component: Enrollment,
    validateSearch: (search) => enrollmentSearchSchema.parse(search),
});

interface WebSocketMessage {
    type: 'image' | 'status' | 'confirmation' | 'error';
    payload: any;
}


function getWebSocketUrl(sessionId: string) {
    const baseUrl = getBaseBackendUrl();
    console.log('Base URL for WebSocket:', baseUrl);
    const url = new URL(baseUrl);
    const wsUrl = url.origin.replace(/^http/, 'ws');
    const finalWsUrl = `${wsUrl}/enrollment/ws?sessionId=${sessionId}`;
    console.log('Final WebSocket URL:', finalWsUrl);
    return finalWsUrl;
}

// ParentData type
interface ParentData {
    id: string;
    parentName: string;
    phoneNumbers: string[];
    occupation: string;
    monthlyIncome: number | null;
    relation: string;
    priority: number;
}


// ---- Types ----
type RouterOutputs = inferRouterOutputs<AppRouter>
type CadetData = RouterOutputs['cadet']['getCadetData']

interface EnrollmentFormValues {
    cadetName: string;
    status: string;
    preferredName: string;
    gender: 'Male' | 'Female';
    dateOfBirthDay: string;
    dateOfBirthMonth: string;
    dateOfBirthYear: string;
    aadhaarCardNumber: string;
    placeOfBirth: string;
    guardianName: string;
    guardianRelation: string;
    occupationOfWhichParent: 'father' | 'mother';
    occupation: string;
    previousSchoolNameAndAddress: string;
    isPreviousSchoolRecognized: boolean;
    previousSchoolClass: number;
    motherTongue: string;
    nationality: string;
    religion: string;
    caste: 'OC' | 'OBC' | 'SC' | 'ST' | 'Others';
    category: 'GEN' | 'GENERAL' | 'DEFENCE' | 'OBC' | 'SC' | 'ST' | 'DEF';
    addressline: string;
    city: string;
    state: string;
    pinCode: string;
    domicile: string;
    phoneNumber: string[];
    parents: ParentData[];
}

function mapStudentDetailsToFormValues(studentDetails: CadetData): EnrollmentFormValues {
    const sd: any = studentDetails;
    let day = '', month = '', year = '';
    if (sd.dateOfBirth) {
        const dateStr = sd.dateOfBirth.toString();
        const [y, m, d] = dateStr.split('-');
        year = y;
        month = m;
        day = d;
    }
    const mappedParents = sd.parents && sd.parents.length > 0
        ? sd.parents.map((parent: any) => ({
            id: parent.id || Date.now().toString(),
            parentName: parent.parentName || '',
            phoneNumbers: parent.phoneNumbers && parent.phoneNumbers.length > 0 ? parent.phoneNumbers : [''],
            occupation: parent.occupation || '',
            monthlyIncome: parent.monthlyIncome,
            relation: parent.relation || '',
            priority: parent.priority || 1,
        }))
        : [
            { id: '1', parentName: '', phoneNumbers: [''], occupation: '', monthlyIncome: null, relation: '', priority: 1 },
            { id: '2', parentName: '', phoneNumbers: [''], occupation: '', monthlyIncome: null, relation: '', priority: 2 },
            { id: '3', parentName: '', phoneNumbers: [''], occupation: '', monthlyIncome: null, relation: '', priority: 3 },
        ];
    return {
        cadetName: sd.cadetName || '',
        preferredName: sd.preferredName || '',
        status: sd.status || 'Admitted',
        gender: (typeof sd.gender === 'string'
            ? (sd.gender.trim().charAt(0).toUpperCase() + sd.gender.trim().slice(1).toLowerCase())
            : 'Male') as 'Male' | 'Female',
        dateOfBirthDay: day,
        dateOfBirthMonth: month,
        dateOfBirthYear: year,
        aadhaarCardNumber: sd.aadhaarCardNumber || '',
        placeOfBirth: sd.placeOfBirth || '',
        guardianName: '',
        guardianRelation: '',
        occupationOfWhichParent: 'father' as const,
        occupation: '',
        previousSchoolNameAndAddress: sd.previousSchoolNameAndAddress || '',
        isPreviousSchoolRecognized: sd.isPreviousSchoolRecognized || false,
        previousSchoolClass: sd.previousSchoolClass || 1,
        motherTongue: sd.motherTongue || '',
        nationality: sd.nationality || '',
        religion: sd.religion || '',
        caste: sd.caste || 'OC' as const,
        category: sd.category || 'GENERAL' as const,
        addressline: sd.addressLine || '',
        city: sd.city || '',
        state: sd.state || '',
        pinCode: sd.pinCode || '',
        domicile: sd.domicile || '',
        phoneNumber: [''],
        parents: mappedParents as ParentData[],
    };
}


function Enrollment() {
    const { sessionId, setSessionId, setStudentDetails } = useEnrollmentStore();

    // Initialise sessionId only once on component mount
    useEffect(() => {
        if (!sessionId) {
            setSessionId(uuidv7());
        }
    }, [sessionId, setSessionId]);

    const [admissionNumber, setAdmissionNumber] = useState('');

    const queryClient = useQueryClient();

    const updateCadetMutationOptions = trpc.cadet.updateCadetData.mutationOptions({
        onSuccess: (_data, variables) => {
            toast.success('Cadet data updated successfully!', {
                description: 'All changes have been saved to the database.',
            });

            // Invalidate the cache for the updated cadet so that views depending on it refetch fresh data
            if (variables?.uniqueId) {
                const queryKey = trpc.cadet.getCadetData.queryOptions({ uniqueId: variables.uniqueId, admissionNumber: undefined }).queryKey;
                queryClient.invalidateQueries({ queryKey });
            }

            // Also invalidate if the component is fetching by admissionNumber
            if (admissionNumber) {
                const queryKeyByAdm = trpc.cadet.getCadetData.queryOptions({ uniqueId: undefined, admissionNumber: `2025-${admissionNumber}` }).queryKey;
                queryClient.invalidateQueries({ queryKey: queryKeyByAdm });
            }

            // You can also choose to refetch immediately if the data is needed right away
            // refetchStudentDetails();
        },
        onError: (error: any) => {
            console.error('Error updating cadet data:', error);
            toast.error('Failed to update cadet data', {
                description: error.message || 'Please try again later.',
            });
        }
    });
    const updateCadetMutation = useMutation(updateCadetMutationOptions);

    // Fetch student details by admission number
    const { data: studentDetails, isLoading: isStudentDetailsLoading, isFetching: isStudentDetailsFetching, refetch: refetchStudentDetails } = useQuery({
        ...trpc.cadet.getCadetData.queryOptions({
            uniqueId: undefined,
            admissionNumber: admissionNumber ? `2025-${admissionNumber}` : undefined,
        }),
        enabled: /^\d{3}$/.test(admissionNumber)
    });

    // Keep the global store in sync with the latest studentDetails
    useEffect(() => {
        setStudentDetails(studentDetails ?? null);
    }, [studentDetails, setStudentDetails]);

    const form = useForm({
        defaultValues: {
            cadetName: '',
            status: 'Admitted',
            preferredName: '',
            gender: 'Male',
            dateOfBirthDay: '',
            dateOfBirthMonth: '',
            dateOfBirthYear: '',
            aadhaarCardNumber: '',
            placeOfBirth: '',
            guardianName: '',
            guardianRelation: '',
            occupationOfWhichParent: 'father',
            occupation: '',
            previousSchoolNameAndAddress: '',
            isPreviousSchoolRecognized: false,
            previousSchoolClass: 1,
            motherTongue: '',
            nationality: '',
            religion: '',
            caste: 'OC',
            category: 'GENERAL',
            addressline: '',
            city: '',
            state: '',
            pinCode: '',
            domicile: '',
            phoneNumber: [''],
            parents: [
                { id: '1', parentName: '', phoneNumbers: [''], occupation: '', monthlyIncome: null, relation: '', priority: 1 },
                { id: '2', parentName: '', phoneNumbers: [''], occupation: '', monthlyIncome: null, relation: '', priority: 2 },
                { id: '3', parentName: '', phoneNumbers: [''], occupation: '', monthlyIncome: null, relation: '', priority: 3 },
            ] as Array<ParentData>,
        },
        onSubmit: async ({ value }) => {
            if (!studentDetails || typeof studentDetails !== 'object' || typeof (studentDetails as any).uniqueId !== 'string') {
                toast.error('No student loaded');
                return;
            }
            updateCadetMutation.mutate({
                uniqueId: (studentDetails as any).uniqueId,
                ...value,
                gender: value.gender as "Male" | "Female",
                caste: value.caste as "OBC" | "SC" | "ST" | "OC" | "Others" | null,
                category: value.category as "GENERAL" | "DEFENCE" | "OBC" | "SC" | "ST" | null
            });
        },
    });


    // Photo-related WebSocket handling has been moved to <PhotoCapture /> to avoid
    // multiple concurrent desktop connections.  Enrollment no longer opens or
    // listens to a WebSocket itself.

    // Keep global form in sync when studentDetails changes -----------------
    useEffect(() => {
        if (studentDetails) {
            const mappedValues = mapStudentDetailsToFormValues(studentDetails);
            Object.entries(mappedValues).forEach(([key, value]) => {
                const field = form.getFieldInfo(key as any);
                if (field) {
                    form.setFieldValue(key as any, value);
                }
            });
        }
    }, [studentDetails, form]);

    return (
        <div className="flex h-screen">
            {/* Left side - Form */}
            <div className="flex-1 h-screen">
                <ScrollArea className="h-full">
                    <div className="p-4">
                        {/* Admission Number Form */}
                        <form className="w-full max-w-md mb-4">
                            <Label htmlFor="admission-number" className="text-md">
                                Admission Number
                            </Label>
                            <div className="flex items-center">
                                <div
                                    className="flex items-center bg-muted border border-input border-r-0 rounded-l-md text-muted-foreground px-3 py-0 h-10 select-none">
                                    2025-
                                </div>
                                <Input
                                    id="admission-number"
                                    type="text"
                                    inputMode="numeric"
                                    maxLength={3}
                                    pattern="\d{3}"
                                    className="border border-input rounded-r-md rounded-l-none w-2/6 text-md h-10"
                                    value={admissionNumber}
                                    onChange={e => setAdmissionNumber(e.target.value)}
                                />
                            </div>
                        </form>

                        {/* WebSocket Session ID */}
                        <div className="text-xs text-gray-400 text-center mb-4">
                            Session ID: {sessionId}
                        </div>

                        {/* Show form if studentDetails is loaded */}
                        {isStudentDetailsLoading && (
                            <div className="text-center my-8">Loading student details...</div>
                        )}
                        {studentDetails && (
                            <Tabs defaultValue="details" className="w-full">
                                <TabsList>
                                    <TabsTrigger value="details">Details</TabsTrigger>
                                    <TabsTrigger value="bed">Bed Allotment</TabsTrigger>
                                    <TabsTrigger value="photo">Photo</TabsTrigger>
                                    <TabsTrigger value="attendance">Attendance Sync</TabsTrigger>
                                </TabsList>
                                <TabsContent value="details" className="mt-4">
                                    <CadetDetails studentDetails={studentDetails} admissionNumber={admissionNumber} />
                                </TabsContent>
                                <TabsContent value="bed" className="mt-4">
                                    <BedAllotment gender={studentDetails.gender as "Male" | "Female"} cadetId={(studentDetails as any).uniqueId} />
                                </TabsContent>
                                <TabsContent value="photo" className="mt-4">
                                    <PhotoCapture />
                                </TabsContent>
                                <TabsContent value="attendance" className="mt-4">
                                    <AttendanceSync />
                                </TabsContent>
                            </Tabs>
                        )}
                    </div>
                </ScrollArea>
            </div>
        </div>
    );
}   